/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-no-bind */
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { string, oneOfType, node, shape, func, bool, number } from 'prop-types'
import Dropdown from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import Field from '@groovehq/internal-design-system/lib/components/Field/Field'
import { styles as fieldStyles } from '@groovehq/internal-design-system/lib/components/Field/Field.styles'
import { styles } from './styles'

const showTagContent = (isPercent, currency) => {
  const text = isPercent ? '%' : currency
  return <span css={styles.tag}>{text}</span>
}

const isNotEmpty = num => {
  if (Number.parseFloat(num) !== 0 && !num) {
    return false
  }
  return true
}

const getOriginalData = discount => {
  // If isPercent is false, get the discount number from data and convert it from cents
  const newValue = isNotEmpty(discount.number) ? discount.number : ''
  // if (!discount.isPercent && isNotEmpty(discount.number)) {
  //   newValue = convertFromCents(newValue)
  // }
  return { ...discount, number: newValue }
}

const PriceDiscount = ({
  children,
  discount,
  currency,
  onDiscountChange,
  price,
  isOrder,
  disabled,
}) => {
  const discountNumberRef = useRef(null)
  const [discountData, setDiscountData] = useState(getOriginalData(discount))
  const { number: numberValue, isPercent, reason } = discountData
  const [numberFeedback, setNumberFeedback] = useState({})
  const [visible, setVisible] = useState(false)
  const [disabledBtn, setDisabledBtn] = useState(true)

  useEffect(
    () => {
      // If the discount exists, enable the save button
      const numberExists = isNotEmpty(discount.number)
      setDisabledBtn(!numberExists)
    },
    [discount.number]
  )

  const handleVisibleChange = useCallback(
    isVisible => {
      if (visible !== isVisible && isVisible === false) {
        setNumberFeedback({})
      }
      setVisible(isVisible)
    },
    [visible]
  )

  const handleClose = useCallback(
    () => {
      handleVisibleChange(false)
    },
    [handleVisibleChange]
  )

  // Check whether the data is ok
  const checkSubmitable = useCallback(() => {
    // Validate number input using browser's default validation
    const numberValidity = discountNumberRef.current.checkValidity()
    setDisabledBtn(!numberValidity)
  }, [])

  // Apply discount to the table
  const applyDiscount = useCallback(
    () => {
      setVisible(false)
      // If isPercent is false, convert back the value to be cents first, then save
      onDiscountChange({ number: parseInt(numberValue, 10), isPercent, reason })
    },
    [numberValue, onDiscountChange, isPercent, reason]
  )

  // Remove discount
  const handleRemove = useCallback(
    () => {
      setVisible(false)
      onDiscountChange(null)
      setNumberFeedback({})
    },
    [onDiscountChange]
  )

  const handleChangeNumber = useCallback(
    e => {
      setDiscountData({ ...discountData, number: e.target.value })
      setNumberFeedback({})
      checkSubmitable()
    },
    [discountData, checkSubmitable]
  )

  const handleBlurNumber = useCallback(() => {
    // TODO
    // const feedback = validateField(
    //   { value: number },
    //   { number: true },
    //   e.target
    // )
    // feedback.help = feedback.help ? 'Invalid number' : ''
    // setNumberFeedback(feedback)
  }, [])

  const handleDiscountTypeChange = useCallback(
    isPercentage => {
      // Change isPercent, need to set number field to empty in case of invalid data
      setDiscountData({ ...discountData, isPercent: isPercentage, number: '' })
      setNumberFeedback({})
      setDisabledBtn(true)
    },
    [discountData]
  )

  const handleChangeReason = useCallback(
    e => {
      setDiscountData({ ...discountData, reason: e.target.value })
    },
    [discountData]
  )

  const footer = useMemo(
    () => {
      if (!visible) return null
      return (
        <>
          {discount.number ? (
            <Button type="warning" size="small" onClick={handleRemove}>
              Remove
            </Button>
          ) : (
            <Button type="tertiary" size="small" onClick={handleClose}>
              Close
            </Button>
          )}
          <Button size="small" onClick={applyDiscount} disabled={disabledBtn}>
            Apply
          </Button>
        </>
      )
    },
    [
      applyDiscount,
      disabledBtn,
      discount.number,
      handleClose,
      handleRemove,
      visible,
    ]
  )

  const overlay = useMemo(
    () => {
      return (
        <div className="grui px-7 pb-10 pt-6">
          <div className="grui mb-8">
            <div css={fieldStyles.labelBox}>
              {`Discount this ${isOrder ? 'Order' : 'item'} by`}
            </div>
            <div className="grui flex">
              <div
                css={[styles.controls, isPercent && styles.percentage]}
                className="grui mr-5"
              >
                <Button
                  type="tertiary"
                  customIcon={currency}
                  onClick={() => handleDiscountTypeChange(false)}
                />
                <Button
                  type="tertiary"
                  customIcon="%"
                  onClick={() => handleDiscountTypeChange(true)}
                />
              </div>
              <Field
                className="grui flex-1"
                tagPosition="left"
                tagContent={showTagContent(isPercent, currency)}
                value={numberValue}
                onChange={handleChangeNumber}
                type="number"
                step="any"
                // Set min and max for validation
                min={0}
                max={isPercent ? 100 : price / 100}
                ref={discountNumberRef}
                onBlur={handleBlurNumber}
                required
                {...numberFeedback}
              />
            </div>
          </div>
          <Field
            label="Reason"
            placeholder="Loyalty discount"
            value={reason || ''}
            onChange={handleChangeReason}
          />
        </div>
      )
    },
    [
      currency,
      handleBlurNumber,
      handleChangeNumber,
      handleChangeReason,
      handleDiscountTypeChange,
      isOrder,
      isPercent,
      numberFeedback,
      numberValue,
      price,
      reason,
    ]
  )

  return (
    <Dropdown
      overlay={overlay}
      footer={footer}
      footerWithBtns
      hasMinWidth
      autoHeight
      visible={visible}
      onVisibleChange={handleVisibleChange}
      disabled={disabled}
    >
      {children}
    </Dropdown>
  )
}

PriceDiscount.propTypes = {
  children: oneOfType([string, node]),
  discount: shape({
    isPercent: bool,
    number: oneOfType[(number, string)],
    reason: string,
  }),
  currency: string,
  onDiscountChange: func,
  price: oneOfType([number, string]),
  /**
   * Is it for order
   */
  isOrder: bool,
  disabled: bool,
}

PriceDiscount.defaultProps = {
  children: undefined,
  discount: {},
  currency: '',
  onDiscountChange() {},
  price: 0,
  isOrder: false,
  disabled: false,
}

export default PriceDiscount
